'use client';

import {
    lazy,
    Suspense,
    type ChangeEvent,
    type FormEvent,
    type ReactElement,
    useCallback,
    useId,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { FormInputField } from '@/shared-ui/molecules';
import { UITarget } from '@/targets/ui-target';

const SearchController = lazy(() => import('./search-controller'));

interface SearchProps {
    initialQuery?: string;
    initiallyOpen?: boolean;
}

export default function Search({ initialQuery = '', initiallyOpen = false }: SearchProps): ReactElement {
    const { t } = useTranslation('search');
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const suggestionsPanelId = `search-suggestions-${useId()}`;
    const [query, setQuery] = useState(initialQuery);
    const [showSuggestions, setShowSuggestions] = useState(initiallyOpen);
    const [isSuggestionsPanelVisible, setIsSuggestionsPanelVisible] = useState(false);
    const [controllerLoaded, setControllerLoaded] = useState(initiallyOpen);

    const openSuggestionsPanel = useCallback(() => {
        setControllerLoaded(true);
        setShowSuggestions(true);
    }, []);

    const closeSuggestionsPanel = useCallback(() => {
        setShowSuggestions(false);
        setIsSuggestionsPanelVisible(false);
    }, []);

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setQuery(e.target.value);
            openSuggestionsPanel();
        },
        [openSuggestionsPanel]
    );

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            const searchQuery = query.trim();
            if (searchQuery) {
                closeSuggestionsPanel();
                void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
                    state: { query: searchQuery },
                });
            }
        },
        [closeSuggestionsPanel, navigate, query]
    );

    return (
        <div ref={containerRef}>
            <form onSubmit={handleSubmit} className="relative z-50">
                <div className="relative">
                    <UITarget targetId="sfcc.header.search.input">
                        <FormInputField.Root>
                            <FormInputField.InputWrapper actionType={'button'}>
                                <FormInputField.Input
                                    onChange={handleInputChange}
                                    onFocus={openSuggestionsPanel}
                                    aria-label={t('placeholder')}
                                    aria-autocomplete="list"
                                    aria-controls={isSuggestionsPanelVisible ? suggestionsPanelId : undefined}
                                    aria-expanded={isSuggestionsPanelVisible}
                                    aria-haspopup="listbox"
                                    role="combobox"
                                    data-testid="header-search"
                                    value={query}
                                    placeholder={t('placeholder')}
                                />
                                <FormInputField.Button type="submit">{t('submit')}</FormInputField.Button>
                            </FormInputField.InputWrapper>
                        </FormInputField.Root>
                    </UITarget>
                </div>
            </form>
            {controllerLoaded ? (
                <Suspense fallback={null}>
                    <SearchController
                        query={query}
                        showSuggestions={showSuggestions}
                        suggestionsPanelId={suggestionsPanelId}
                        containerRef={containerRef}
                        closeSuggestions={closeSuggestionsPanel}
                        onPanelVisibilityChange={setIsSuggestionsPanelVisible}
                    />
                </Suspense>
            ) : null}
        </div>
    );
}
